import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useRateContext } from "../Rate/Context/RateContext";

export type RateOptionHighlightKey =
  | "schedule"
  | "memberOnly"
  | "payWhatYouCan"
  | "groupTicket"
  | "categories"
  | "taxReceipt";

interface RateOptionHighlightContextValue {
  register: (rateId: string, optionKey: string, callback: () => void) => () => void;
  trigger: (rateId: string, optionKey: RateOptionHighlightKey) => void;
}

const noop = () => undefined;

const RateOptionHighlightContext = createContext<RateOptionHighlightContextValue>({
  register: () => noop,
  trigger: noop,
});

export const RateOptionHighlightProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const callbacksRef = useRef(new Map<string, () => void>());

  const value = useMemo<RateOptionHighlightContextValue>(
    () => ({
      register: (rateId, optionKey, callback) => {
        const key = `${rateId}:${optionKey}`;
        callbacksRef.current.set(key, callback);
        return () => {
          if (callbacksRef.current.get(key) === callback) {
            callbacksRef.current.delete(key);
          }
        };
      },
      trigger: (rateId, optionKey) => {
        callbacksRef.current.get(`${rateId}:${optionKey}`)?.();
      },
    }),
    []
  );

  return <RateOptionHighlightContext.Provider value={value}>{children}</RateOptionHighlightContext.Provider>;
};

export const useTriggerRateOptionHighlight = (): RateOptionHighlightContextValue["trigger"] =>
  useContext(RateOptionHighlightContext).trigger;

export const useRateOptionHighlight = (optionKey: string): { isPending: boolean; clear: () => void } => {
  const { register } = useContext(RateOptionHighlightContext);
  const { rateId } = useRateContext();
  const [isPending, setIsPending] = useState(false);

  useEffect(() => register(rateId, optionKey, () => setIsPending(true)), [register, rateId, optionKey]);

  return { isPending, clear: () => setIsPending(false) };
};
